import SocialLogin from '@/components/Auth/SocialLogin';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { FormCard } from '@/components/FormCard';
import { GradientText } from '@/components/GradientText';
import { Link } from '@/components/Link';
import { PasswordInput } from '@/components/PasswordInput';
import { PhoneInput } from '@/components/PhoneInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { AuthLayout } from '@/layouts/AuthLayout';
import { useAuthStore } from '@/stores/useAuthStore';
import i18n from '@/translations/config';
import type { EmailRegisterCredentials } from '@/types/auth';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

export const SignUp = () => {
  const { t } = useTranslation();
  const lang = i18n.language;
  const { emailRegister } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const initialValues: EmailRegisterCredentials = {
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
    newsletter: false,
    rgpd: false,
  };

  const validationSchema = yup.object().shape({
    firstname: yup
      .string()
      .required(t('common:validation.firstname.required'))
      .matches(
        /^[a-zA-ZÀ-ÖØ-öø-ÿ]+([ -][a-zA-ZÀ-ÖØ-öø-ÿ]+){0,3}[ ]*$/,
        t('common:validation.firstname.format'),
      ),
    lastname: yup
      .string()
      .required(t('common:validation.lastname.required'))
      .matches(
        /^[a-zA-ZÀ-ÖØ-öø-ÿ]+([ -][a-zA-ZÀ-ÖØ-öø-ÿ]+){0,3}[ ]*$/,
        t('common:validation.lastname.format'),
      ),
    email: yup
      .string()
      .email(t('common:validation.email.format'))
      .required(t('common:validation.email.required')),
    password: yup
      .string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d\s:]).{6,}$/,
        t('common:validation.password.format'),
      )
      .required(t('common:validation.password.required')),
    password_confirmation: yup
      .string()
      .oneOf(
        [yup.ref('password')],
        t('common:validation.password.confirmation'),
      )
      .required(t('common:validation.password.confirmation')),
    phone: yup
      .string()
      .matches(/^[+][1-9]\d{1,14}$/, t('common:validation.phone.format'))
      .required(t('common:validation.phone.required')),
    newsletter: yup.boolean(),
    rgpd: yup.boolean().oneOf([true], t('signUp:validation.rgpd.required')),
  });

  const handleRegister = async (
    values: EmailRegisterCredentials,
    { setSubmitting, setErrors }: FormikHelpers<EmailRegisterCredentials>,
  ) => {
    try {
      await emailRegister(values);
      navigate(`/${lang}/verify-email`);
    } catch (error: unknown) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t('signUp:welcome')}
      welcomeText={t('signUp:welcome-text')}
    >
      <FormCard title={t('signUp:title')}>
        {location.state?.from && (
          <div className="mb-4 rounded-lg bg-green-50 p-4 text-sm text-green-700">
            {t('accessory:login-or-register-before')}
          </div>
        )}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleRegister}
        >
          {({ isSubmitting, touched, errors, values, setFieldValue }) => (
            <Form className="w-full space-y-2 md:space-y-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-3">
                <div className="space-y-1">
                  <TextInput
                    label={t('signUp:label.firstname')}
                    name="firstname"
                    type="text"
                    placeholder={t('signUp:placeholder.firstname')}
                    error={!!touched.firstname && !!errors.firstname}
                  />
                </div>
                <div className="space-y-1">
                  <TextInput
                    label={t('signUp:label.lastname')}
                    name="lastname"
                    type="text"
                    placeholder={t('signUp:placeholder.lastname')}
                    error={!!touched.lastname && !!errors.lastname}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <TextInput
                  label={t('signUp:label.email')}
                  name="email"
                  type="email"
                  placeholder={t('signUp:placeholder.email')}
                  error={!!touched.email && !!errors.email}
                />
              </div>

              <div className="space-y-1">
                <PhoneInput
                  defaultCountry="BE"
                  label={t('signUp:label.phone')}
                  name="phone"
                  placeholder="000 00 00 00"
                  error={!!touched.phone && !!errors.phone}
                />
              </div>

              <div className="space-y-1">
                <PasswordInput
                  label={t('signUp:label.password')}
                  name="password"
                  placeholder={t('signUp:placeholder.password')}
                  error={!!touched.password && !!errors.password}
                />
              </div>

              <div className="space-y-1">
                <PasswordInput
                  label={t('signUp:label.password-confirmation')}
                  name="password_confirmation"
                  placeholder={t('signUp:placeholder.password-confirmation')}
                  error={
                    !!touched.password_confirmation &&
                    !!errors.password_confirmation
                  }
                />
              </div>

              <div className="mt-2 flex flex-col gap-2">
                <Checkbox
                  id="newsletter"
                  label={t('signUp:label.newsletter')}
                  checked={values.newsletter}
                  onChange={() =>
                    setFieldValue('newsletter', !values.newsletter)
                  }
                />
                <Checkbox
                  id="rgpd"
                  label={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('signUp:label.rgpd').replace(
                          t('signUp:privacy.link'),
                          `<a href="https://linko-connect.com/politique-de-confidentialite/" target="_blank" rel="noopener noreferrer" class="text-sky-600 hover:text-sky-800 hover:underline">${t('signUp:privacy.link')}</a>`,
                        ),
                      }}
                    />
                  }
                  checked={values.rgpd}
                  onChange={() => setFieldValue('rgpd', !values.rgpd)}
                  error={touched.rgpd && errors.rgpd}
                />
              </div>

              <Button
                type="submit"
                loading={isSubmitting}
                className="mt-2 !w-full bg-gradient-to-r from-sky-600 to-sky-500 py-2 text-white shadow-md hover:from-sky-700 hover:to-sky-600 focus:ring-2 focus:ring-sky-300 md:mt-3 md:py-2.5"
              >
                {t('signUp:submit')}
              </Button>
            </Form>
          )}
        </Formik>
      </FormCard>
      <SocialLogin />
      <Text className="mt-6 text-center text-sm text-gray-600 md:mt-8">
        {t('signUp:sign-in.description')}{' '}
        <Link
          to={`/${lang}`}
          className="font-medium text-sky-600 hover:text-sky-800"
        >
          <GradientText>{t('signUp:sign-in.link')}</GradientText>
        </Link>
      </Text>
    </AuthLayout>
  );
};
